<script setup lang="ts">
import { computed } from 'vue';
import type { Bookmark } from '@ilteducation/annotations-types';
import useLocale from '../../composables/useLocale';
import BookmarkForm from './BookmarkForm.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
library.add(faXmark);

const showDialog = defineModel<boolean>({ default: false });

type Emits = {
  (e: 'save', name: string, comment: string): void;
  (e: 'remove'): void;
};
const emit = defineEmits<Emits>();

type Props = {
  bookmark?: Bookmark; // If provided, we're in edit mode
  isSaving?: boolean;
  isRemoving?: boolean;
};
const props = defineProps<Props>();

const { t } = useLocale();

const state = computed<'edit' | 'new'>(() => (props.bookmark?.id ? 'edit' : 'new'));

const title = computed(() => {
  return state.value === 'edit' ? t('label_edit_bookmark') : t('label_new_bookmark');
});
</script>

<template>
  <v-dialog v-model="showDialog" max-width="560" :persistent="true" @keydown.esc="showDialog = false">
    <v-card color="surface" class="bookmark-dialog">
      <v-card-title class="d-flex align-center justify-end position-relative">
        <div class="text-h5 text-center w-100 position-absolute left-0 right-0">
          {{ title }}
        </div>
        <v-btn
          size="48"
          variant="text"
          :title="t('label_close')"
          :aria-label="t('label_close')"
          @click="showDialog = false"
        >
          <FontAwesomeIcon size="sm" icon="far fa-xmark" />
        </v-btn>
      </v-card-title>
      <v-divider class="mb-2"></v-divider>

      <BookmarkForm
        :name="bookmark?.name"
        :comment="bookmark?.comment?.type === 'text' ? bookmark.comment.content : ''"
        :state="state"
        :is-saving="isSaving"
        :is-removing="isRemoving"
        @save="(name, comment) => emit('save', name, comment)"
        @remove="emit('remove')"
        @cancel="showDialog = false"
      />
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss"></style>
